import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Divider,
  Typography,
  CircularProgress,
  Autocomplete,
  IconButton
} from '@mui/material';
import { Save, Trash, UserPen, UserPlus, FileText, FileSpreadsheet, FileImage, X } from 'lucide-react';
import { FilterFieldSx, DropDownSX, TextFeaildSx } from '../../../../constants/style';
import DatePicker from '../../common/DatePicker/DatePicker';
import { saveIssues, deleteIssues } from '../../../../apis/Issues';
import { fetchClients } from '../../../../apis/Clients';
import CommonDialog from '../../common/CommonDialog';
import AddClient from '../../Clients/AddClient';
import { userScreenAccess, isDeleteAllowed, formatDate } from '../../../../utils';
import Tooltip from '../../../../utils/Tooltip';
import dayjs from 'dayjs';
import { uploadImage } from '../../../../apis/FileUpload';
import { FILE_ACCEPT_EXTENTION } from '../../../../constants/constant';
import { IsAdmins } from '../../../../utils';

/* ── Empty form state ─────────────────────────────────────────────────────── */
const emptyForm = {
  clientGUID: 0,
  issueGUID: 0,
  productGUID: 0,
  productName: '',
  title: '',
  description: '',
  priorityId: 0,
  priorityName: '',
  statusId: 11001,
  statusName: '',
  userGUID: 0,
  issueTypeId: 0,
  assignedTo: '',
  dueDate: null,
  reportedDate: dayjs(),
  imageUrl: '',
  imageFiles: [],
  imagePreviews: []
};

/* ── Validation ───────────────────────────────────────────────────────────── */
const validate = (form) => {
  const errors = {};
  if (!form.productGUID) {
    errors.productGUID = 'Product is required.';
  }
  if (!form.title.trim()) {
    errors.title = 'Title is required.';
  }
  if (!form.description.trim()) {
    errors.description = 'Description is required.';
  }
  if (!form.priorityId) {
    errors.priorityId = 'Priority is required.';
  }
  if (!form.statusId) {
    errors.statusId = 'Status is required.';
  }
  if (!form.assignedTo) {
    errors.assignedTo = 'Assigned To is required.';
  }
  if (!form.dueDate) {
    errors.dueDate = 'Due Date is required.';
  }
  if (!form.issueTypeId) {
    errors.issueTypeId = 'Issue Type is required.';
  }
  if (!form.clientGUID) {
    errors.clientGUID = 'Client is required.';
  }
  return errors;
};

// eslint-disable-next-line react/prop-types
const AddIssues = ({
  clients = [],
  products = [],
  statuses = [],
  priorities = [],
  users = [],
  issueTypeOptions = [],
  issueData = null,
  userAccess = null,
  onRefresh,
  onClose
}) => {
  const isEditMode = Boolean(issueData);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clientOptions, setClientOptions] = useState(clients || []);
  const [clientLoading, setClientLoading] = useState(false);
  const isDeleteAllowed_Check = isEditMode ? isDeleteAllowed() : false;
  const [openDialog, setOpenDialog] = useState(false);

  const isEditAllowed = isEditMode ? userAccess?.hasEditPermission : userAccess?.hasAddPermission;

  const [loading, setLoading] = useState(false);
  const isClientPermission = userScreenAccess('/clients');
  const isAdmin = IsAdmins();
  const completedId = [11003, 11004]

  useEffect(() => {
    if (issueData) {
      setForm({
        issueGUID: issueData.issueGUID || 0,
        productGUID: issueData.productGUID || 0,
        productName: issueData.productName || '',
        title: issueData.title || '',
        description: issueData.description || '',
        priorityId: issueData.priorityId || 0,
        priorityName: issueData.priorityName || '',
        statusId: issueData.statusId || 0,
        statusName: issueData.statusName || '',
        userGUID: issueData.userGUID || 0,
        assignedTo: issueData.assignedTo || '',
        reportedDate: issueData.reportedDate ? new Date(issueData.reportedDate) : null,
        dueDate: issueData.dueDate ? new Date(issueData.dueDate) : null,
        imageUrl: issueData.imageUrl || '',
        imagePreviews: issueData?.filePaths?.length
          ? issueData.filePaths.map(p => {
            const ext = p.split('.').pop()?.toLowerCase();
            return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? p : null;
          })
          : (issueData.imageUrl ? [issueData.imageUrl] : []),
        clientGUID: issueData.clientGUID || 0,
        issueTypeId: issueData.issueTypeId || 0,
        imageFiles: issueData?.filePaths || []
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [issueData]);

  const handleClientChange = async (event, value) => {
    setForm((prev) => ({ ...prev, clientGUID: value?.clientGUID }));
  };

  // Fetch combo options on component mount
  // useEffect(() => {
  //   const fetchOptions = async () => {
  //     setLoading(true);
  //     try {
  //       const [productsData, statusesData, prioritiesData, usersData] = await Promise.all([
  //         fetchProductListCombo(),
  //         fetchGlobalTypesCombo(MastersId.USER_NON_USER),
  //         fetchGlobalTypesCombo(MastersId.CALL_TYPE),
  //         fetchUsersCombo()
  //       ]);
  //       setProducts(productsData || []);
  //       setStatuses(statusesData || []);
  //       setPriorities(prioritiesData || []);
  //       setUsers(usersData || []);
  //     } catch (err) {
  //       console.log('Error fetching combo options:', err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //   fetchOptions();
  // }, []);

  // ── Field change ───────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const next = { ...form, [field]: value };
    setForm(next);

    // Live-validate touched fields
    if (touched[field]) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  // Handle Autocomplete change
  const handleAutocompleteChange = (field, idField) => (event, value) => {
    const next = {
      ...form,
      [field]: value?.[field] || '',
      [idField]: value?.[idField] || 0
    };
    setForm(next);

    if (touched[idField]) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, [idField]: errs[idField] }));
    }
  };

  const autoCompleteChange = (field, valueFeild) => (event, value) => {
    const next = {
      ...form,
      [field]: value?.[valueFeild] || ''
    };
    setForm(next);

    if (touched[valueFeild]) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, [valueFeild]: errs[valueFeild] }));
    }
  };

  // Handle Date change
  const handleDateChange = (date) => {
    setForm((prev) => ({ ...prev, dueDate: date }));

    if (touched.dueDate) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, dueDate: errs.dueDate }));
    }
  };

  // Handle Date change
  const handleReportedDateChange = (date) => {
    setForm((prev) => ({ ...prev, reportedDate: date }));

    if (touched.reportedDate) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, reportedDate: errs.reportedDate }));
    }
  };

  const isImageFile = (file) => {
    if (!file) return false;
    if (typeof file === 'string') {
      const ext = file.split('.').pop()?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    }
    return file.type?.startsWith('image/');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (file) => {
    if (!file) return { icon: FileText, color: '#757575', bg: '#EEEEEE' };
    const fileName = typeof file === 'string' ? file.split('/').pop() : file.name;
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return { icon: FileText, color: '#E53935', bg: '#FFEBEE' };       // Red
      case 'doc':
      case 'docx':
        return { icon: FileText, color: '#1565C0', bg: '#E3F2FD' };       // Blue
      case 'xls':
      case 'xlsx':
      case 'csv':
        return { icon: FileSpreadsheet, color: '#2E7D32', bg: '#E8F5E9' }; // Green
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return { icon: FileImage, color: '#7B1FA2', bg: '#F3E5F5' };       // Purple
      case 'txt':
        return { icon: FileText, color: '#616161', bg: '#F5F5F5' };        // Grey
      default:
        return { icon: FileText, color: '#757575', bg: '#EEEEEE' };        // Default
    }
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    // Generate preview URLs only for image files, null for others
    const previews = selectedFiles.map((file) =>
      isImageFile(file) ? URL.createObjectURL(file) : null
    );

    setForm((prev) => ({
      ...prev,
      imageFiles: [...prev.imageFiles, ...selectedFiles],
      imagePreviews: [...prev.imagePreviews, ...previews]
    }));
  };

  const handleRemoveFile = (index) => {
    setForm((prev) => {
      const nextPreviews = [...prev.imagePreviews];
      const nextFiles = [...prev.imageFiles];
      // Revoke object URL to free memory
      if (nextPreviews[index]) URL.revokeObjectURL(nextPreviews[index]);
      nextPreviews.splice(index, 1);
      nextFiles.splice(index, 1);
      return { ...prev, imageFiles: nextFiles, imagePreviews: nextPreviews };
    });
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(form);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Touch all fields to reveal errors
    const allTouched = Object.fromEntries(Object.keys(form).map((k) => [k, true]));
    setTouched(allTouched);

    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    let uploadedFiles = [];
    let newFiles = []
    let alreadyFiles = []
    console.log('form.imageFiles', form.imageFiles);

    form.imageFiles.forEach((file) => {
      if (typeof file === 'string') {
        const fileName = file.replace("Uploads/", "");
        alreadyFiles.push(fileName)
      } else {
        newFiles.push(file)
      }
    })

    const formData = new FormData();
    if (newFiles.length > 0) {
      newFiles.forEach((file) => {
        formData.append('file', file);
      });

      const uploadResponse = await uploadImage(formData, '', '');

      // Assuming uploadResponse has the expected structure
      if (!uploadResponse?.isSuccess) {
        return;
      }
      if (uploadResponse?.files?.length > 0) {
        uploadedFiles = uploadResponse.files;
      }
    }

    const finalFilePaths = [...alreadyFiles, ...uploadedFiles];

    setSaving(true);
    try {
      const payload = {
        issueGUID: form.issueGUID,
        productGUID: form.productGUID,
        title: form.title.trim(),
        description: form.description.trim(),
        priorityId: form.priorityId,
        statusId: form.statusId,
        userGUID: form.userGUID,
        assignedTo: form.assignedTo,
        reportedDate: form.reportedDate ? formatDate(form.reportedDate) : null,
        dueDate: form.dueDate ? formatDate(form.dueDate) : null,
        filePaths: finalFilePaths,
        imagePreviews: form.imagePreviews,
        imageFiles: form.imageFiles,
        clientGUID: form.clientGUID,
        issueTypeId: form.issueTypeId
      };

      const successMsg = isEditMode ? 'Issue updated successfully' : 'Issue created successfully';

      const response = await saveIssues(payload, successMsg, '');
      if (response?.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to save issue:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Has any visible error ──────────────────────────────────────────────────
  const hasErrors = Object.keys(validate(form)).length > 0;

  return (
    <>
      <Box component="form" noValidate autoComplete="off">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            opacity: loading ? 0.6 : 1,
            pointerEvents: loading ? 'none' : 'auto'
          }}
        >
          {/* Row 1: Client Name | Title | Description */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 2.5
            }}
          >
            {/* Client Name */}
            <Box
              sx={{
                flex: '1 1 50%',
                minWidth: '200px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1
              }}>
              <Autocomplete
                size="small"
                fullWidth
                options={clientOptions}
                value={clientOptions.find((c) => c.clientGUID === form.clientGUID) || null}
                onChange={handleClientChange}
                disablePortal={false}
                getOptionLabel={(option) => option?.clientName || ''}
                isOptionEqualToValue={(option, value) => option?.clientGUID === value?.clientGUID}
                loading={clientLoading}
                slotProps={{
                  paper: {
                    sx: DropDownSX
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client Name"
                    placeholder="Select Client"
                    sx={FilterFieldSx}
                    onBlur={handleBlur('clientGUID')}
                    error={touched.clientGUID && Boolean(errors.clientGUID)}
                  />
                )}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Title */}
            <Box>
              <TextField
                fullWidth
                label="Title *"
                placeholder="Enter issue title"
                value={form.title}
                onChange={handleChange('title')}
                onBlur={handleBlur('title')}
                error={touched.title && Boolean(errors.title)}
                size="small"
                sx={FilterFieldSx}
                slotProps={{
                  input: {
                    readOnly: !isEditAllowed
                  }
                }}
              />
            </Box>

            {/* Description */}
            <Box>
              <TextField
                fullWidth
                label="Description *"
                placeholder="Enter issue description"
                value={form.description}
                onChange={handleChange('description')}
                onBlur={handleBlur('description')}
                error={touched.description && Boolean(errors.description)}
                multiline
                minRows={1}
                maxRows={3}
                size="small"
                sx={TextFeaildSx}
                slotProps={{
                  input: {
                    readOnly: !isEditAllowed
                  }
                }}
              />
            </Box>
          </Box>

          {/* Row 2: Product | Priority */}
          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}
          >

            <Box>
              <Autocomplete
                size="small"
                fullWidth
                options={issueTypeOptions}
                value={issueTypeOptions.find((p) => p.globalTypeID === form.issueTypeId) || null}
                onChange={autoCompleteChange('issueTypeId', 'globalTypeID')}
                disablePortal={false}
                disableClearable={false}
                getOptionLabel={(option) => option?.globalTypeName || ''}
                isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
                sx={{ flex: 1 }}
                slotProps={{
                  paper: {
                    sx: DropDownSX
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Issue Type *"
                    placeholder="Select Issue Type"
                    sx={FilterFieldSx}
                    onBlur={handleBlur('issueTypeId')}
                    error={touched.issueTypeId && Boolean(errors.issueTypeId)}
                  />
                )}
              />
            </Box>
            {/* Product */}
            <Box>
              <Autocomplete
                size="small"
                fullWidth
                options={products}
                value={products.find((p) => p.productGUID === form.productGUID) || null}
                onChange={autoCompleteChange('productGUID', 'productGUID')}
                getOptionLabel={(option) => option?.productName || ''}
                isOptionEqualToValue={(option, value) => option?.productGUID === value?.productGUID}
                onBlur={handleBlur('productGUID')}
                slotProps={{
                  paper: {
                    sx: DropDownSX
                  }
                }}
                readOnly={!isEditAllowed}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product *"
                    placeholder="Select product"
                    error={touched.productGUID && Boolean(errors.productGUID)}
                    sx={FilterFieldSx}
                  />
                )}
              />
            </Box>

            {/* Priority */}
            <Box>
              <Autocomplete
                size="small"
                fullWidth
                options={priorities}
                value={priorities.find((p) => p.globalTypeID === form.priorityId) || null}
                onChange={autoCompleteChange('priorityId', 'globalTypeID')}
                getOptionLabel={(option) => option?.globalTypeName || ''}
                isOptionEqualToValue={(option, value) =>
                  option?.globalTypeID === value?.globalTypeID
                }
                onBlur={handleBlur('priorityId')}
                readOnly={!isEditAllowed}
                slotProps={{
                  paper: {
                    sx: DropDownSX
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Priority *"
                    placeholder="Select priority"
                    error={touched.priorityId && Boolean(errors.priorityId)}
                    sx={FilterFieldSx}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Row 3: Status | Assigned To */}
          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}
          >
            {/* Status */}
            <Box>
              <Autocomplete
                size="small"
                fullWidth
                options={statuses}
                value={statuses.find((s) => s.globalTypeID === form.statusId) || null}
                onChange={autoCompleteChange('statusId', 'globalTypeID')}
                getOptionLabel={(option) => option?.globalTypeName || ''}
                isOptionEqualToValue={(option, value) =>
                  option?.globalTypeID === value?.globalTypeID
                }
                slotProps={{
                  paper: {
                    sx: DropDownSX
                  }
                }}
                onBlur={handleBlur('statusId')}
                readOnly={!isEditAllowed}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status *"
                    placeholder="Select status"
                    error={touched.statusId && Boolean(errors.statusId)}
                    sx={FilterFieldSx}
                  />
                )}
              />
            </Box>

            {/* Assigned To */}
            <Box>
              <Autocomplete
                size="small"
                fullWidth
                options={users}
                value={users.find((u) => u.userName === form.assignedTo) || null}
                onChange={autoCompleteChange('assignedTo', 'userName')}
                getOptionLabel={(option) => option?.userName || ''}
                isOptionEqualToValue={(option, value) => option?.userName === value?.userName}
                onBlur={handleBlur('assignedTo')}
                readOnly={!isEditAllowed}
                slotProps={{
                  paper: {
                    sx: DropDownSX
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned To *"
                    placeholder="Select user"
                    error={touched.assignedTo && Boolean(errors.assignedTo)}
                    sx={FilterFieldSx}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Row 4: Reported Date | Due Date */}
          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}
          >
            {/* Reported Date */}
            <Box sx={{ width: '100%' }}>
              <DatePicker
                mode="single"
                label="Reported Date *"
                placeholder="Select Reported"
                value={form.reportedDate}
                onChange={handleReportedDateChange}
                height={36}
                width="100%"
                readOnly={!isEditAllowed}
              />
              {touched.reportedDate && errors.reportedDate && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {errors.reportedDate}
                </Typography>
              )}
            </Box>

            {/* Due Date */}
            <Box sx={{ width: '100%' }}>
              <DatePicker
                mode="single"
                label="Due Date *"
                placeholder="Select due date"
                value={form.dueDate}
                onChange={handleDateChange}
                height={36}
                width="100%"
                readOnly={!isEditAllowed}
              />
              {touched.dueDate && errors.dueDate && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {errors.dueDate}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Row 5: Image Upload (full width) */}
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                sx={{
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 2,
                  py: 1
                }}
              >
                Upload File
                <input
                  hidden
                  accept={FILE_ACCEPT_EXTENTION}
                  type="file"
                  multiple
                  onChange={handleFileChange}
                />
              </Button>

              {form.imageFiles.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {form.imageFiles.map((file, index) => {
                    const preview = form.imagePreviews[index];
                    const isImage = isImageFile(file);
                    return (
                      <Box
                        key={file.name + index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1.5,
                          p: 1,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        {/* Thumbnail or file icon */}
                        {isImage && preview ? (
                          <Box
                            component="img"
                            src={preview}
                            alt={file.name}
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 1,
                              objectFit: 'cover',
                              flexShrink: 0
                            }}
                          />
                        ) : (() => {
                          const { icon: Icon, color, bg } = getFileIcon(file);
                          return (
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: bg,
                                flexShrink: 0
                              }}
                            >
                              <Icon size={24} color={color} />
                            </Box>
                          );
                        })()}

                        {/* File info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap title={typeof file === 'string' ? file.split('/').pop() : file.name}>
                            {typeof file === 'string' ? file.split('/').pop() : file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {typeof file === 'string' ? 'Uploaded file' : formatFileSize(file.size)}
                          </Typography>
                        </Box>

                        {/* Remove button */}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveFile(index)}
                        >
                          <X size={18} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Upload files for the issue (optional).
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Actions ─────────────────────────────────────────────────────── */}
        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          {/* <Button
          variant="contained"
          size="small"
          startIcon={
            deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
          }
          disabled={deleting}
          onClick={handleDelete}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 100,
            background: '#e05757',
            '&:hover': { background: '#6d6464' },
            boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
            display: !isEditMode || !isDeleteAllowed_Check ? 'none' : 'inline-flex'
          }}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button> */}

          {(!completedId.includes(form.statusId) || isAdmin) &&
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
              disabled={saving || (hasErrors && Object.keys(touched).length > 0) || loading}
              onClick={handleSave}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 100,
                background: 'var(--primary, #36c95f)',
                '&:hover': { background: 'var(--primary-dark, #27ae60)' },
                boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
                display: !isEditAllowed ? 'none' : 'inline-flex'
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>}
        </Box>
      </Box>
    </>
  );
};

export default AddIssues;
